/**
 * May this session look at its CUSTOMERS' connections?
 *
 * The Connections page used to show only the partner's own pool while still
 * rendering a Customer column, so the column was empty on every row and the
 * only way to reach a customer's connection was to impersonate that customer
 * from the widget builder. That is a dead end whenever the widget itself is
 * what is broken, which is exactly when someone goes looking for the
 * connection. The page now has both views; this decides whether to offer the
 * second one.
 *
 * THE FRONTEND GATE MUST MATCH THE BACKEND GATE (engineering rule 7). The
 * server's is parseConnectionListScope in services/api/src/routes/connections.ts
 * and it refuses `?scope=customers` on exactly these two grounds:
 *
 *   - a role that is not account-tier (operator / viewer / end_user reach their
 *     own end org and nothing else, so "my customers" is not a set they have);
 *   - a tenant-scoped session, whose acting org is the tenant while orgId names
 *     the partner — for one of those, the partner's children are its SIBLINGS.
 *
 * The dashboard cannot see the token's parentOrgId, so it tests the same thing
 * off the org's TYPE: an `end_org` workspace is a customer, and a customer has
 * no customers. That is deliberately NOT "an org with a parent" — every partner
 * account is parented to org_platform, so the parent test would hide this from
 * practically everyone (see the same note in lib/auth.tsx).
 *
 * An UNKNOWN orgType is a no, not a yes. It carries no claim on the token and is
 * filled in from the memberships response, so it is undefined for the first
 * render of every session — and defaulting that to "show it" is what would flash
 * the tab at a tenant admin, who is an `admin` inside an `end_org` and whom the
 * server refuses. Same least-privilege-until-resolved rule as `roleResolved`.
 */

import type { SystemRole } from "./rbac";

/** Mirrors ACCOUNT_TIER_ROLES in packages/rbac/src/engine.ts. */
const ACCOUNT_TIER_ROLES: ReadonlySet<string> = new Set<SystemRole>([
  "platform_admin",
  "owner",
  "admin",
  "developer",
]);

export function canViewCustomerConnections(input: {
  role: SystemRole | undefined;
  /** company | personal | partner | end_org | platform */
  orgType: string | undefined;
}): boolean {
  if (!input.role || !ACCOUNT_TIER_ROLES.has(input.role)) return false;
  if (!input.orgType) return false;
  return input.orgType !== "end_org";
}
